import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { FILTER_FIELD_SX } from "app/components/Layout/filterToolbarStyles";

const DATE_FORMAT = "MM/DD/YYYY";

const FILTER_TRIGGER_SX = {
  width: "100%",
  justifyContent: "space-between",
  textTransform: "none" as const,
  fontSize: "0.75rem",
  fontWeight: 500,
  color: "#64748b",
  borderColor: "#e2e8f0",
  bgcolor: "#fff",
  px: 1,
  py: 0.35,
  minHeight: 30,
  "&:hover": {
    bgcolor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
};

const FILTER_TRIGGER_ACTIVE_SX = {
  color: "#334155",
  borderColor: "#94a3b8",
  bgcolor: "#e2e8f0",
};

type TextColumnFilterProps = {
  label: string;
  value: string;
  placeholder?: string;
  onApply: (value: string) => void;
};

export function UnassignedOrdersTextColumnFilter({
  label,
  value,
  placeholder = "Search",
  onApply,
}: TextColumnFilterProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState(value);
  const open = Boolean(anchorEl);
  const active = value.trim().length > 0;

  useEffect(() => {
    if (!open) {
      setDraft(value);
    }
  }, [open, value]);

  const handleApply = () => {
    onApply(draft.trim());
    setAnchorEl(null);
  };

  const handleClear = () => {
    setDraft("");
    onApply("");
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          ...FILTER_TRIGGER_SX,
          ...(active ? FILTER_TRIGGER_ACTIVE_SX : {}),
        }}
      >
        <Typography noWrap sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
          {active ? value : "Filter"}
        </Typography>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              p: 1.5,
              width: 260,
              borderRadius: "10px",
              border: "1px solid #e5e9ef",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#334155", mb: 1 }}>
          Filter {label}
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder={placeholder}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleApply();
            }
          }}
          sx={{ ...FILTER_FIELD_SX }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.25 }}>
          <Button size="small" onClick={handleClear} sx={{ textTransform: "none", color: "#64748b" }}>
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              bgcolor: "#7ec597",
              boxShadow: "none",
              "&:hover": { bgcolor: "#6db486", boxShadow: "none" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}

type DateColumnFilterProps = {
  scheduledFrom: Dayjs | null;
  scheduledTo: Dayjs | null;
  onApply: (from: Dayjs | null, to: Dayjs | null) => void;
};

export function UnassignedOrdersDateColumnFilter({
  scheduledFrom,
  scheduledTo,
  onApply,
}: DateColumnFilterProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFrom, setDraftFrom] = useState<Dayjs | null>(scheduledFrom);
  const [draftTo, setDraftTo] = useState<Dayjs | null>(scheduledTo);
  const open = Boolean(anchorEl);
  const active = Boolean(scheduledFrom || scheduledTo);

  useEffect(() => {
    if (!open) {
      setDraftFrom(scheduledFrom);
      setDraftTo(scheduledTo);
    }
  }, [open, scheduledFrom, scheduledTo]);

  const summary = active
    ? [
        scheduledFrom?.format(DATE_FORMAT),
        scheduledTo?.format(DATE_FORMAT),
      ]
        .filter(Boolean)
        .join(" – ")
    : "Filter";

  const handleApply = () => {
    onApply(
      draftFrom?.startOf("day") ?? null,
      draftTo?.startOf("day") ?? null
    );
    setAnchorEl(null);
  };

  const handleClear = () => {
    setDraftFrom(null);
    setDraftTo(null);
    onApply(null, null);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          ...FILTER_TRIGGER_SX,
          ...(active ? FILTER_TRIGGER_ACTIVE_SX : {}),
        }}
      >
        <Typography noWrap sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
          {summary}
        </Typography>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              p: 1.5,
              width: 280,
              borderRadius: "10px",
              border: "1px solid #e5e9ef",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#334155", mb: 1 }}>
          Filter scheduled date
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <DatePicker
              label="From"
              format={DATE_FORMAT}
              value={draftFrom}
              onChange={(value) => {
                const nextFrom = value?.startOf("day") ?? null;
                setDraftFrom(nextFrom);
                if (draftTo && nextFrom && draftTo.isBefore(nextFrom, "day")) {
                  setDraftTo(nextFrom);
                }
              }}
              slotProps={{
                textField: { size: "small", fullWidth: true, sx: FILTER_FIELD_SX },
              }}
            />
            <DatePicker
              label="To"
              format={DATE_FORMAT}
              value={draftTo}
              minDate={draftFrom ?? undefined}
              onChange={(value) => setDraftTo(value?.startOf("day") ?? null)}
              slotProps={{
                textField: { size: "small", fullWidth: true, sx: FILTER_FIELD_SX },
              }}
            />
          </Box>
        </LocalizationProvider>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.25 }}>
          <Button size="small" onClick={handleClear} sx={{ textTransform: "none", color: "#64748b" }}>
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              bgcolor: "#7ec597",
              boxShadow: "none",
              "&:hover": { bgcolor: "#6db486", boxShadow: "none" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export function UnassignedOrdersFilterPlaceholder() {
  return (
    <Typography sx={{ fontSize: "0.75rem", color: "#cbd5e1", px: 0.5 }}>—</Typography>
  );
}
